import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { RangeOption } from "../../constants/epicLeaderboard";
import type { PlayerEpicStatsResponse } from "../../types/epicLeaderboard";
import { getEpicColor } from "../../utils/epicColors";
import {
  fetchPlayerEpicStats,
  peekPlayerEpicStats
} from "../../utils/playerEpicApi";
import { playerPath } from "../../utils/playerLink";
import { rangeToStartDate } from "../../utils/rangeToStartDate";
import SkeletonBlock from "../common/SkeletonBlock";
import "../epicLeaderboard/epicLeaderboard.css";
import "./pk/pk.css";

type Props = {
  player: string;
  range: RangeOption;
};

// Fastest clear per small-group epic, linking through to the Epics tab. Says
// nothing for a player with no runs in the range.
const BestEpicTimes = ({ player, range }: Props) => {
  const [result, setResult] = useState<{
    key: string;
    data: PlayerEpicStatsResponse | null;
  } | null>(null);
  const key = `${player.toLowerCase()}|${range}`;

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      try {
        const data = await fetchPlayerEpicStats(
          player,
          rangeToStartDate(range),
          controller.signal
        );
        if (!controller.signal.aborted) setResult({ key, data });
      } catch (err) {
        if (controller.signal.aborted) return;
        console.error("Failed to fetch best epic times", err);
        setResult({ key, data: null });
      }
    };
    load();
    return () => controller.abort();
  }, [player, range, key]);

  // Already fetched (e.g. visiting the Epics tab first): no skeleton flash.
  const cached = peekPlayerEpicStats(player, rangeToStartDate(range));
  const data = cached ?? (result?.key === key ? result.data : undefined);

  if (data === undefined) {
    return (
      <div className="mt-4" role="status" aria-label="Loading best epic times">
        <SkeletonBlock height={26} width={170} className="mb-3" />
        <SkeletonBlock height={96} style={{ borderRadius: 12 }} />
      </div>
    );
  }
  if (!data || data.epics.length === 0) return null;

  return (
    <section className="pk-section">
      <h3 className="pk-section-title">BEST EPIC TIMES</h3>
      <div className="pk-card">
        {data.epics.map((epic) => (
          <div key={epic.epic} className="pk-feed-row">
            <div className="pk-feed-main">
              <span
                className="pk-legend-dot"
                style={{ backgroundColor: getEpicColor(epic.epic) }}
              />
              {epic.epic}
            </div>
            <div className="pk-tile-name" style={{ fontVariantNumeric: "tabular-nums" }}>
              {epic.fastest}
            </div>
            <div className="pk-feed-date">
              {epic.runCount} {epic.runCount === 1 ? "run" : "runs"}
            </div>
          </div>
        ))}
        <div className="mt-2">
          <Link
            className="pk-link"
            to={playerPath({ player, range, tab: "epics" })}
          >
            See all epic stats
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BestEpicTimes;
